import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClipboardCheck, 
  faChevronRight,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faQuestionCircle,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

const TestHistoryList = ({ historyData, onTestClick, theme }) => {
  const getScoreColor = (score) => {
    if (score >= 70) return theme?.primary?.green?.[500];
    if (score >= 50) return theme?.primary?.yellow?.[500];
    return theme?.primary?.red?.[500];
  };

  const getScoreGradient = (score) => {
    if (score >= 70) {
      return `linear-gradient(135deg, ${theme?.primary?.green?.[400]}, ${theme?.primary?.green?.[600]})`;
    }
    if (score >= 50) {
      return `linear-gradient(135deg, ${theme?.primary?.yellow?.[400]}, ${theme?.primary?.yellow?.[600]})`;
    }
    return `linear-gradient(135deg, ${theme?.primary?.red?.[400]}, ${theme?.primary?.red?.[600]})`;
  };

  const getScoreBadgeStyle = (score) => {
    if (score >= 70) {
      return {
        background: `linear-gradient(135deg, ${theme?.primary?.green?.[100]}30, ${theme?.primary?.green?.[200]}40)`,
        border: `1px solid ${theme?.primary?.green?.[300]}60`,
        color: theme?.primary?.green?.[700],
      };
    }
    if (score >= 50) {
      return {
        background: `linear-gradient(135deg, ${theme?.primary?.yellow?.[100]}30, ${theme?.primary?.yellow?.[200]}40)`,
        border: `1px solid ${theme?.primary?.yellow?.[300]}60`,
        color: theme?.primary?.yellow?.[700],
      };
    }
    return {
      background: `linear-gradient(135deg, ${theme?.primary?.red?.[100]}30, ${theme?.primary?.red?.[200]}40)`,
      border: `1px solid ${theme?.primary?.red?.[300]}60`,
      color: theme?.primary?.red?.[700],
    };
  };

  // Flatten all test data with dates
  const allTests = historyData.flatMap(group => 
    group.data.map(test => ({
      ...test,
      dateGroup: group.title,
      date: moment(group.title, "MMM DD, YYYY HH:mm:ss")
    }))
  );

  const styles = {
    container: {
      background: theme?.background?.secondary,
      borderRadius: '1.25rem',
      padding: '2rem',
      border: `0px solid ${theme?.primary?.gray?.[100]}`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      marginBottom: '2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
    },
    subtitle: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      marginTop: '0.25rem',
    },
    testCount: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      background: theme?.background?.secondary,
      padding: '0.5rem 1rem',
      borderRadius: '2rem',
      border: `1px solid ${theme?.borders?.primary}`,
    },
    contentContainer: {
      flex: 1,
    },
    cardsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '1.25rem',
      paddingBottom: '1rem',
    },
    testCard: {
      background: theme?.background?.tertiary,
      borderRadius: '1rem',
      padding: '1.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: `0px solid ${theme?.primary?.gray?.[200]}`,
      position: 'relative',
      overflow: 'hidden',
    },
    testCardHover: {
      transform: 'translateY(-5px)',
      boxShadow: theme?.shadows?.lg,
      borderColor: theme?.borders?.hover,
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: '1rem',
    },
    cardLeft: {
      flex: 1,
    },
    testName: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      marginBottom: '0.5rem',
      lineHeight: '1.3',
    },
    dateInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.75rem',
      color: theme?.text?.secondary,
      marginBottom: '1rem',
    },
    scoreCard: {
      textAlign: 'center',
      minWidth: '70px',
    },
    scoreBadge: {
      padding: '0.5rem 0.75rem',
      borderRadius: '0.75rem',
      fontSize: '1.25rem',
      fontWeight: '700',
      marginBottom: '0.25rem',
    },
    scoreLabel: {
      fontSize: '0.625rem',
      color: theme?.text?.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '0.75rem',
      marginBottom: '1rem',
    },
         statItem: {
       display: 'flex',
       flexDirection: 'column',
       alignItems: 'center',
       justifyContent: 'center',
       gap: '0.25rem',
       fontSize: '0.8rem',
       color: theme?.text?.secondary,
       padding: '0.75rem 0.5rem',
       borderRadius: '0.5rem',
       border: `1px solid ${theme?.borders?.primary}`,
       textAlign: 'center',
     },
    statIcon: {
      fontSize: '0.875rem',
      width: '14px',
    },
    statValue: {
      fontWeight: '600',
      color: theme?.text?.primary,
    },
    cardFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingTop: '1rem',
      borderTop: `1px solid ${theme?.borders?.primary}`,
    },
    timeInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.8rem',
      color: theme?.text?.secondary,
    },
    viewButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.8rem',
      color: theme?.primary?.blue?.[600],
      fontWeight: '500',
      padding: '0.25rem',
      borderRadius: '0.25rem',
      transition: 'all 0.2s ease',
    },
    progressBar: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: theme?.borders?.primary,
    },
    progressFill: {
      height: '100%',
      transition: 'width 0.5s ease',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1rem',
      color: theme?.text?.secondary,
    },
    emptyIcon: {
      fontSize: '3rem',
      marginBottom: '1rem',
      opacity: 0.5,
    },
  };

  if (!allTests.length) {
    return (
      <motion.div
        style={styles.container}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Test History</h2>
            <p style={styles.subtitle}>Your recent test performance</p>
          </div>
        </div>
        <div style={styles.emptyState}>
          <FontAwesomeIcon icon={faClipboardCheck} style={styles.emptyIcon} />
          <p>No tests taken yet</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Test History</h2>
          <p style={styles.subtitle}>Your recent test performance</p>
        </div>
        <div style={styles.testCount}>
          {allTests.length} test{allTests.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div style={styles.contentContainer}>
        <div style={styles.cardsGrid}>
          {allTests.map((test, index) => (
            <motion.div
              key={`${test.test_name}-${index}`}
              style={styles.testCard}
              whileHover={styles.testCardHover}
              onClick={() => onTestClick(test)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, duration: 0.4 }}
            >
              <div style={styles.cardHeader}>
                <div style={styles.cardLeft}>
                  <div style={styles.testName}>{test.test_name}</div>
                  <div style={styles.dateInfo}>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>
                      {typeof test.date === 'string' ? test.date : test.date.format("MMM, DD YYYY HH:mm:ss")}
                    </span>
                  </div>
                </div>
                
                <div style={styles.scoreCard}>
                  <div style={{
                    ...styles.scoreBadge,
                    ...getScoreBadgeStyle(test.score),
                  }}>
                    {test.score}%
                  </div>
                  <div style={styles.scoreLabel}>Score</div>
                </div>
              </div>
              
                             <div className="grid grid-cols-3 gap-2">
                 <div style={{
                   ...styles.statItem,
                 }}>
                   <div style={{...styles.statValue, color: theme?.primary?.green?.[500]}}>{test.correct}</div>
                   <div>correct</div>
                 </div>
                 
                 <div style={{
                   ...styles.statItem,
                 }}>
                   <div style={{...styles.statValue, color: theme?.primary?.red?.[500]}}>{test.incorrect}</div>
                   <div>incorrect</div>
                 </div>
                 
                 <div style={{
                   ...styles.statItem,
                 }}>
                   <div style={{...styles.statValue, color: theme?.primary?.yellow?.[500]}}>{test.unanswered || 0}</div>
                   <div>unanswered</div>
                 </div>
               </div>
              
              <div style={styles.progressBar}>
                <div style={{
                  ...styles.progressFill,
                  width: `${test.score}%`,
                  background: getScoreGradient(test.score),
                }} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default TestHistoryList;